import React, { CSSProperties, useEffect, useState } from 'react';
import { useAppDispatch } from '../../../store';
import { updateAISettings } from '../../../store/actions/SettingsActions';
import { fetchMatterDropdowns } from '../../../store/actions/Matters/MatterActions';
import { useTranslation } from 'react-i18next';
import { AVAILABLE_JURISDICTIONS } from '../../Matters/matterConstants';
import Dropdown, { DropdownOption } from '../../shared/TailwindComponents/Dropdown';
import SettingsSection from '../framework/SettingsSection';
import logger from '../../../utils/logger';
import { withJurisdictionKeys, withLanguage } from '../../../utils/aiPreferences';
import useAIPreferences from '../../../hooks/useAIPreferences';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import { PiGenderFemaleLight, PiGenderMaleLight, PiSparkleLight } from 'react-icons/pi';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, aiSettingsTourSteps } from '../../shared/FeatureTour';

/**
 * Settings → Personal → AI.
 *
 * Three preferences, each written the moment it changes — so the section is
 * `instant`, and the three mini cards that used to wrap one dropdown apiece
 * are now three fields in one grid, labelled by the controls themselves.
 */
const AISettingsComponent: React.FC<{ style?: CSSProperties; backgroundColor?: string }> = ({
  style,
}) => {
  const dispatch = useAppDispatch();
  // The same store-backed read every service uses — this tab is just the one
  // place that also writes it. No fetch of its own: the platform bootstrap
  // hydrated the section before this screen could mount.
  const aiPreferences = useAIPreferences();
  const { t } = useTranslation();
  const [selectedAILanguage, setSelectedAILanguage] = useState<string>('');
  const [selectedVoice, setSelectedVoiceState] = useState<string>('');
  const [selectedJurisdiction, setSelectedJurisdiction] = useState<string>('');
  const [languageOptions, setLanguageOptions] = useState<DropdownOption[]>([]);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'ai-settings',
    steps: aiSettingsTourSteps,
    autoStartDelay: 600,
  });

  const [jurisdictionOptions, setJurisdictionOptions] = useState<DropdownOption[]>(AVAILABLE_JURISDICTIONS);
  /**
   * Both async reads this tab depends on — the option lists and the saved
   * preferences themselves. Until they settle, the three selects would render
   * empty and then pop in a value, which is indistinguishable from "you have
   * no preference". Show the shape instead, and clear the flag on failure too
   * so a dead endpoint leaves a usable (empty) form rather than a stuck one.
   */
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const isLoading = isLoadingOptions || aiPreferences.isHydrating;

  const voiceOptions = [
    { label: t('Default'), value: 'default' },
    { label: t('Male'), value: 'male', icon: <PiGenderMaleLight /> },
    { label: t('Female'), value: 'female', icon: <PiGenderFemaleLight /> },
    { label: t('Neutral'), value: 'neutral' },
  ];

  // Fetch language and jurisdiction options from backend (same as matter creation)
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const dropdownsData = await dispatch(fetchMatterDropdowns());
        if (dropdownsData) {
          if (dropdownsData.languages) {
            setLanguageOptions(dropdownsData.languages);
          }
          // Jurisdictions come from storage-account discovery (backend); fall
          // back to the static list only when the backend list is empty.
          if (dropdownsData.jurisdictions && dropdownsData.jurisdictions.length > 0) {
            setJurisdictionOptions(dropdownsData.jurisdictions);
          }
        }
      } catch (error) {
        logger.error('Failed to fetch AI settings dropdown options');
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadDropdowns();
  }, [dispatch]);

  // Initialize component with actual values from preferences.
  // The UI shows the real backend value or a genuinely empty control — it must
  // never invent a jurisdiction, because the very next save would write that
  // invented value back as if the user had chosen it.
  useEffect(() => {
    setSelectedAILanguage(aiPreferences.language);
    setSelectedVoiceState(aiPreferences.voice || 'default');
    setSelectedJurisdiction(aiPreferences.jurisdiction);
  }, [aiPreferences.language, aiPreferences.voice, aiPreferences.jurisdiction]);

  const handleSelectAILanguage = (selectedValue: string | string[]) => {
    const value = Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;
    if (value !== selectedAILanguage) {
      setSelectedAILanguage(value);
      saveAISettings({
        language: value,
        jurisdiction: selectedJurisdiction,
        speaker_voice: selectedVoice,
      });
    }
  };

  const handleSelectAIVoice = (selectedValue: string | string[]) => {
    const value = Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;
    if (value !== selectedVoice) {
      setSelectedVoiceState(value);
      saveAISettings({
        language: selectedAILanguage,
        jurisdiction: selectedJurisdiction,
        speaker_voice: value,
      });
    }
  };

  const handleSelectJurisdiction = (selectedValue: string | string[]) => {
    const value = Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;
    if (value !== selectedJurisdiction) {
      setSelectedJurisdiction(value);
      saveAISettings({
        language: selectedAILanguage,
        jurisdiction: value,
        speaker_voice: selectedVoice,
      });
    }
  };

  const saveAISettings = async (settings: {
    jurisdiction: string;
    language: string;
    speaker_voice: string;
  }) => {
    // Dual-write: legacy `jurisdiction` + authoritative `CountryOfJurisdiction`.
    // An untouched, unset jurisdiction goes out as '' — never as a hardcode,
    // and the language is normalized the same way.
    dispatch(
      updateAISettings(
        withLanguage(withJurisdictionKeys({
          jurisdiction: settings.jurisdiction,
          language: settings.language,
          speaker_voice: settings.speaker_voice,
        }))
      ) as any
    );
  };

  return (
    <>
      <div style={style}>
        <SettingsSection
          title={t('Assistant preferences')}
          description={t('Customize AI preferences for a personalized and efficient experience.')}
          icon={<PiSparkleLight />}
          saveMode="instant"
        >
          {isLoading ? (
            <Skeleton variant="form" rows={3} inset="none" />
          ) : (
          <div data-tour-id="ai-settings-cards" className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Dropdown
              id="ai-jurisdiction"
              label={t('Jurisdiction')}
              description={t('Select your legal framework.')}
              options={jurisdictionOptions}
              value={selectedJurisdiction}
              placeholder={t('Select Jurisdiction')}
              onChange={handleSelectJurisdiction}
              fullWidth
            />

            <Dropdown
              id="ai-language"
              label={t('Language')}
              description={t('Set the language for AI communication.')}
              options={languageOptions}
              value={selectedAILanguage}
              placeholder={t('Select Language')}
              onChange={handleSelectAILanguage}
              fullWidth
            />

            <Dropdown
              id="ai-voice"
              label={t('Speaker Voice')}
              description={t('Choose a voice tailored to your preferences.')}
              options={voiceOptions}
              value={selectedVoice}
              placeholder={t('Select Voice')}
              onChange={handleSelectAIVoice}
              fullWidth
            />
          </div>
          )}
        </SettingsSection>
      </div>

      <FeatureTour
        steps={aiSettingsTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </>
  );
};

export default AISettingsComponent;
